import React, { useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import useToast from '../hooks/useToast';
import { useSocket } from '../context/SocketContext';

const CustomFieldsManager = () => {
  const { showSuccess, showError } = useToast();
  const socket = useSocket();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ 
    name: '', 
    label: '', 
    type: 'text', 
    options: '', 
    required: false, 
    order: 0 
  });

  // ✅ React Query: Fetch custom fields
  const {
    data: fields = [],
    isLoading,
    refetch: refetchFields,
  } = useApiQuery(
    ['custom-fields'],
    () => api.get('/custom-fields').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: () => showError('Failed to load fields'),
    }
  );

  // ✅ React Query: Create field mutation
  const createMutation = useApiMutation(
    (data) => api.post('/custom-fields', data).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Field created');
        resetForm();
        refetchFields();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to create field');
      },
    }
  );

  // ✅ React Query: Update field mutation
  const updateMutation = useApiMutation(
    ({ id, data }) => api.put(`/custom-fields/${id}`, data).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Field updated');
        resetForm();
        refetchFields();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to update field');
      },
    }
  );

  // ✅ React Query: Delete field mutation
  const deleteMutation = useApiMutation(
    (id) => api.delete(`/custom-fields/${id}`).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Field deleted');
        refetchFields();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to delete field');
      },
    }
  );

  // ✅ Socket listener for real-time updates
  React.useEffect(() => {
    if (!socket) return;
    const handleChange = () => refetchFields();
    socket.on('customFieldsChanged', handleChange);
    return () => socket.off('customFieldsChanged', handleChange);
  }, [socket, refetchFields]);

  // ✅ Reset form
  const resetForm = useCallback(() => {
    setEditingId(null);
    setForm({ name: '', label: '', type: 'text', options: '', required: false, order: 0 });
  }, []);

  // ✅ Handle submit
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    const payload = {
      ...form,
      options: form.type === 'select' ? form.options.split(',').map(s => s.trim()) : [],
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }, [editingId, form, createMutation, updateMutation]);

  // ✅ Handle delete
  const handleDelete = useCallback((id) => {
    if (window.confirm('Delete this field?')) {
      deleteMutation.mutate(id);
    }
  }, [deleteMutation]);

  // ✅ Start edit
  const startEdit = useCallback((field) => {
    setEditingId(field._id);
    setForm({
      name: field.name,
      label: field.label,
      type: field.type,
      options: field.options ? field.options.join(',') : '',
      required: field.required,
      order: field.order,
    });
  }, []);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading custom fields...</p>
        </div>
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-[#2b3445]">Custom User Fields</h1>
      
      {/* Form Card */}
      <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6 mb-6">
        <h2 className="text-lg font-semibold text-[#2b3445] mb-4">
          {editingId ? 'Edit Field' : 'Add New Field'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Name *</label>
              <input
                type="text"
                placeholder="e.g., phone"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                required
                disabled={isSubmitting}
              />
              <p className="text-xs text-[#708090] mt-1">Unique identifier (no spaces)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
              <input
                type="text"
                placeholder="e.g., Phone Number"
                value={form.label}
                onChange={e => setForm({ ...form, label: e.target.value })}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
              <select
                value={form.type}
                onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                disabled={isSubmitting}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="email">Email</option>
                <option value="textarea">Textarea</option>
                <option value="select">Select</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
              <input
                type="number"
                placeholder="Display order"
                value={form.order}
                onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {form.type === 'select' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Options (comma separated)</label>
              <input
                type="text"
                placeholder="Option 1, Option 2, Option 3"
                value={form.options}
                onChange={e => setForm({ ...form, options: e.target.value })}
                className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.required}
                onChange={e => setForm({ ...form, required: e.target.checked })}
                className="h-4 w-4 text-[#1471b9] border-[#dae1e7] rounded focus:ring-[#1471b9]"
                disabled={isSubmitting}
              />
              Required field
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#1471b9] text-white px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-[#0f5a9e] transition disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (editingId ? 'Update Field' : 'Create Field')}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                disabled={isSubmitting}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-gray-300 transition disabled:opacity-50"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Fields Table */}
      <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#dae1e7]">
          <h2 className="text-lg font-semibold text-[#2b3445]">Existing Fields</h2>
        </div>
        {fields.length === 0 ? (
          <div className="p-8 text-center text-[#708090]">
            No custom fields created yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#dae1e7]">
              <thead className="bg-[#f8f9fa]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Required</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[#708090] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#dae1e7]">
                {fields.map(f => (
                  <tr key={f._id} className="hover:bg-[#f8f9fa] transition-colors">
                    <td className="px-4 py-3 text-sm text-[#2b3445] font-mono">{f.name}</td>
                    <td className="px-4 py-3 text-sm text-[#2b3445]">{f.label}</td>
                    <td className="px-4 py-3 text-sm text-[#708090]">
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">{f.type}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        f.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {f.required ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => startEdit(f)}
                        disabled={isSubmitting}
                        className="text-[#1471b9] hover:text-[#0f5a9e] mr-3 text-sm font-medium transition disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(f._id)}
                        disabled={deleteMutation.isPending}
                        className="text-red-500 hover:text-red-700 text-sm font-medium transition disabled:opacity-50"
                      >
                        {deleteMutation.isPending && deleteMutation.variables === f._id ? '...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFieldsManager;